import {
  ConfigurationItem,
  CreateTradeGeneralInfo,
  CreateTradeLimits,
  EditSignalLimits,
  FreeTextTradeData,
  GetCreateTradeGeneralDataResponse,
  NameAndOptions,
  Option,
  OrderStatus,
  OrderTypes,
  PrecisionConfig,
} from "@shared/types";
import {
  CreateTradeOrdersData,
  ErrorTypes,
  ErrorTypeToDisplay,
  InputOrderData,
  InputOrdersData,
  OrderData,
  OrdersData,
  OrderSectionErrors,
  OrdersErrors,
  ValidationOrderData,
  ValidationOrdersData,
} from "@shared/features/trades/common/types";
import { getItemByName, getName } from "@shared/features/configuration/utils";
import { ORDER_TYPE_TO_TITLE_DISPLAY } from "@shared/common/constants";
import {
  getNormalizedPercentages,
  getOrdersAnnotation,
  getOrdersDefaultSortOrder,
  getStopCurrentPriceValidation,
  orderHasStatus,
} from "@shared/features/trades/common/utils";
import { round, toFixed } from "@shared/utils/numbers";
import React from "react";
import {
  CreateTradeSections,
  ExtraInfo,
  StrategyInfo,
  TradeTypes,
} from "@shared/features/tradingTerminal/common/types";
import {
  TRAILING_PERCENT_ITEM_NAME,
  TRAILING_STOP_ITEM_NAME,
  TRAILING_STOP_TYPE_ITEM_NAME,
  TRAILING_TYPE_ITEM_NAME,
  TRIGGER_ITEM_NAME,
} from "@shared/features/configuration/constants";
import { findIndex, isEqual, set, sumBy } from "lodash";
import {
  EditTargetOrZone,
  ValidationTargetData,
  ValidationTargetsData,
} from "@shared/features/signals/EditSignal/types";
import { EditOrderData } from "@shared/features/trades/EditTrade/types";
import { AppThemeType } from "@shared/common/Themes/AppTheme";
import {
  CREATE_TRADE_KEY_TO_GENERAL_OPTIONS_NAME,
  DEFAULT_BREAKOUT_ENTRY_NUM_ORDERS,
  DEFAULT_NUM_ORDERS,
  EQUAL_ZONE_PRICES_NUM_ORDERS,
} from "@shared/features/tradingTerminal/common/constants";

export const getDataByOrderType = (
  data: ConfigurationItem[],
  orderType: OrderTypes
) => {
  return data?.find((item: ConfigurationItem) =>
    getName(item)
      .toLowerCase()
      .includes(
        ORDER_TYPE_TO_TITLE_DISPLAY[orderType].replace("-", " ").toLowerCase()
      )
  );
};

const getPricesValues = (
  orderType: OrderTypes,
  isShort: boolean,
  isBreakout: boolean,
  priceRange?: { min: number; max: number }
) => {
  const { min, max } = priceRange || { min: 0, max: 0 };
  let initialValue = min;
  let additionalValue = max;
  if (
    (orderType === OrderTypes.ENTRY &&
      ((!isShort && !isBreakout) || (isShort && isBreakout))) ||
    (orderType === OrderTypes.TAKE_PROFIT && isShort)
  ) {
    initialValue = max;
    additionalValue = min;
  }

  return { initialValue, additionalValue };
};

export const getDistributedTargets = (
  orderType: OrderTypes,
  numOrders: number,
  isShort: boolean,
  isBreakout: boolean,
  priceRange?: { min: number; max: number }
) => {
  const { min, max } = priceRange || { min: 0, max: 0 };
  const priceRangeSize = max - min;
  const { initialValue, additionalValue } = getPricesValues(
    orderType,
    isShort,
    isBreakout,
    priceRange
  );

  let ordersPrices = [initialValue];
  if (numOrders > 2) {
    const interval = priceRangeSize / (numOrders - 1);
    ordersPrices = ordersPrices.concat(
      Array.from(Array(numOrders - 2)).map(
        (_, index) => min + interval * (index + 1)
      )
    );
  }
  if (numOrders > 1) {
    ordersPrices.push(additionalValue);
  }

  const sortOrder = getOrdersDefaultSortOrder(orderType, isShort, isBreakout);
  return [...ordersPrices].sort((a, b) => sortOrder * (a - b));
};

export const getStrategyOrdersPercentages = (
  strategy: number[],
  numOrders: number,
  percentPrecision: number
) => {
  let ordersPercentages = strategy.slice(0, numOrders);
  return getNormalizedPercentages(ordersPercentages, percentPrecision);
};

export const buildOrders = (
  numOrders: number,
  strategy: number[],
  amount: number,
  percentPrecision: number,
  pricePrecision: number,
  amountPrecision: number,
  orderType: OrderTypes,
  baselineEntryPrice: number,
  isShort: boolean,
  isBreakout: boolean,
  priceRange?: { min: number; max: number },
  priceTargets?: number[],
  percentTargets?: number[]
) => {
  let ordersPrices = priceTargets;

  if (!ordersPrices) {
    ordersPrices = getDistributedTargets(
      orderType,
      numOrders,
      isShort,
      isBreakout,
      priceRange
    );
  }

  let percentagesToUse = percentTargets?.slice(0, numOrders);

  if (!percentagesToUse) {
    percentagesToUse = getStrategyOrdersPercentages(
      strategy,
      numOrders,
      percentPrecision
    );
  }

  return percentagesToUse.map((percent, index) => {
    const orderPrice = toFixed(ordersPrices![index], pricePrecision);
    const orderData: InputOrderData = {
      percent: percent.toString(),
      price: orderPrice.toString(),
    };
    if (orderType === OrderTypes.ENTRY) {
      const orderAmount = (percent / 100) * (amount || 0);
      orderData.amount = toFixed(orderAmount, amountPrecision).toString();
    } else {
      orderData.distance =
        baselineEntryPrice > 0
          ? calculateDistanceFromPrice(
              orderPrice,
              baselineEntryPrice,
              percentPrecision
            ).toString()
          : "";
    }
    return orderData;
  });
};

export const getParsedEntries = (entries: InputOrderData[]) => {
  return entries.map((order) => {
    return { price: Number(order.price), ratio: Number(order.percent) / 100 };
  });
};

export const getMinAndMaxPrices = (
  currentPrice: number,
  pricePrecision: number,
  priceFactorLimits?: { min: number; max: number },
  limits?: CreateTradeLimits | EditSignalLimits
) => {
  let minPrice = 0;
  let maxPrice = 0;
  if (priceFactorLimits) {
    minPrice = currentPrice / priceFactorLimits.min;
    maxPrice = currentPrice * priceFactorLimits.max;
  } else if (limits?.price) {
    minPrice = limits.price.min || 0;
    maxPrice = limits.price.max || 0;
  }
  return {
    min: toFixed(minPrice, pricePrecision),
    max: toFixed(maxPrice, pricePrecision),
  };
};

export const calculateDistanceFromPrice = (
  orderPrice: number,
  price: number,
  precision?: number
) => Math.abs(round(((orderPrice - price) / price) * 100, precision));

export const setStateByType = <T extends OrderTypes | CreateTradeSections>(
  setState: React.Dispatch<React.SetStateAction<Record<T, any>>>,
  type: T,
  value: any
) => {
  setState((prevState) => ({
    ...prevState,
    [type]: value,
  }));
};

const getOrdersPrices = (orders: InputOrderData[]) =>
  orders.map((order) => {
    return {
      price: parseFloat(order.price),
    };
  });

export const createTradeOrdersAnnotations = (
  orders: Record<OrderTypes, InputOrderData[]>,
  precisionConfig: PrecisionConfig | undefined,
  theme: AppThemeType
) => {
  return [
    ...getOrdersAnnotation(
      getOrdersPrices(orders[OrderTypes.ENTRY]),
      theme.colors.green,
      precisionConfig,
      undefined,
      undefined,
      OrderTypes.ENTRY
    ),
    ...getOrdersAnnotation(
      getOrdersPrices(orders[OrderTypes.TAKE_PROFIT]),
      theme.colors.red,
      precisionConfig,
      undefined,
      undefined,
      OrderTypes.TAKE_PROFIT
    ),
    ...getOrdersAnnotation(
      getOrdersPrices(orders[OrderTypes.STOP]),
      theme.colors.orange,
      precisionConfig,
      undefined,
      undefined,
      OrderTypes.STOP
    ),
  ];
};

export const findValueByName = (options: Option[], name?: string) => {
  return options.find((option) => option.name === name)?.value;
};

export const findNameByValue = (
  options?: Option[],
  value?: string | number
) => {
  return options?.find((option) => option.value === value)?.name;
};

export const getFormattedTrailingStopItem = (item?: ConfigurationItem) => {
  const trailingStopItem = getItemByName(TRAILING_STOP_ITEM_NAME, item);
  const relatedChildrenItem = {
    data: [
      getItemByName(TRAILING_STOP_TYPE_ITEM_NAME, trailingStopItem),
      getItemByName(TRAILING_PERCENT_ITEM_NAME, trailingStopItem),
    ],
    name: [{ constraints: null, outcome: TRAILING_TYPE_ITEM_NAME }],
  };
  return {
    ...trailingStopItem,
    data: [
      relatedChildrenItem,
      getItemByName(TRIGGER_ITEM_NAME, trailingStopItem),
    ],
  };
};

export const getCreateTradeOrder = (
  order: OrderData,
  precisionConfig?: PrecisionConfig
) => {
  const percent = toFixed(order.percent, precisionConfig?.percent);
  return {
    ...order,
    percent,
    amount: toFixed(order.amount, precisionConfig?.amount),
    ratio: percent / 100,
  };
};

export const getCreateTradeOrders = (
  ordersData: OrdersData,
  precisionConfig?: PrecisionConfig
) => {
  return Object.entries(ordersData).reduce((acc, [orderType, orders]) => {
    acc[orderType as keyof OrdersData] = orders
      .filter((order: OrderData) => order.percent !== 0)
      .map((order: OrderData) => getCreateTradeOrder(order, precisionConfig));
    return acc;
  }, {} as CreateTradeOrdersData);
};

const getNumberOrderData = (inputOrderData: InputOrderData): OrderData =>
  Object.entries(inputOrderData).reduce(
    (numberOrder, [orderKey, orderValue]) => {
      numberOrder[orderKey as keyof OrderData] = orderValue
        ? parseFloat(orderValue)
        : 0;
      return numberOrder;
    },
    {} as OrderData
  );

export const getNumberOrdersData = (ordersData: InputOrdersData) =>
  Object.entries(ordersData).reduce((acc, [orderType, orders]) => {
    acc[orderType as keyof OrdersData] = orders.map((order: InputOrderData) =>
      getNumberOrderData(order)
    );
    return acc;
  }, {} as OrdersData);

export const getDefaultOrdersSectionErrors = (
  orders: OrderData[]
): OrderSectionErrors => ({
  section: {},
  orders: orders.reduce((acc, _, index) => {
    acc[index] = {};
    return acc;
  }, {} as { [key: number]: ErrorTypeToDisplay }),
});

export const addError = (
  orderErrors: OrderSectionErrors,
  errorType: ErrorTypes,
  orderIndex?: number,
  displayMessage?: string
) => {
  orderErrors.section[errorType] = displayMessage;
  if (orderIndex !== undefined) {
    set(orderErrors.orders, [orderIndex, errorType], displayMessage);
  }
};

const validateEntryOrdersAmounts = (
  entries: OrderData[],
  errors: OrderSectionErrors,
  availableAmount: number,
  getMinAmountByPrice: (price: number) => number
) => {
  entries.forEach((order, index) => {
    const minAmount = getMinAmountByPrice(order.price);
    const orderAmount = order.amount!;
    if (
      orderAmount !== 0 &&
      (orderAmount < minAmount || orderAmount > availableAmount)
    ) {
      addError(errors, ErrorTypes.INVALID_ENTRY_ORDER_AMOUNT, index);
    }
  });
};

const validateOrdersDuplicatePrices = (
  ordersData: (ValidationOrderData | ValidationTargetData)[],
  errors: OrderSectionErrors
) => {
  const shouldValidateOrder = (
    order: ValidationOrderData | ValidationTargetData
  ) => !("status" in order) || order.status === OrderStatus.PENDING;
  let ordersPrices = ordersData
    .filter((order) => shouldValidateOrder(order))
    .map((order) => order.price);
  const duplicatePrices: (number | [number, number])[] =
    findDuplicates(ordersPrices);
  ordersData.forEach((order, index) => {
    if (
      shouldValidateOrder(order) &&
      duplicatePrices.some((price) => isEqual(price, order.price))
    ) {
      addError(errors, ErrorTypes.DUPLICATE_PRICE, index);
    }
  });
};

const validateOrdersPricesRange = (
  ordersData: OrderData[] | ValidationTargetData[],
  errors: OrderSectionErrors,
  minPrice: number,
  maxPrice: number
) => {
  const checkPriceRange = (price: number, index: number) => {
    if (price < minPrice || price > maxPrice) {
      addError(errors, ErrorTypes.INVALID_ORDER_PRICE, index);
    }
  };

  ordersData.forEach((order, index) => {
    if (Array.isArray(order.price)) {
      checkPriceRange(order.price[0], index);
      checkPriceRange(order.price[1], index);
    } else {
      checkPriceRange(order.price, index);
    }
  });
};

const validateEntryOrderRelativePrice = (
  entryPrice: number,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  takeProfitsMinMaxPrices: { min: number; max: number }
) => {
  const errors: ErrorTypes[] = [];
  let error: ErrorTypes | undefined;
  if (isShort && entryPrice <= takeProfitsMinMaxPrices.max) {
    error = ErrorTypes.SHORT_ENTRY_BELOW_TAKE_PROFIT;
  } else if (!isShort && entryPrice >= takeProfitsMinMaxPrices.min) {
    error = ErrorTypes.LONG_ENTRY_ABOVE_TAKE_PROFIT;
  }
  if (error) errors.push(error);
  if (isBreakout && isShort && entryPrice >= currentPrice) {
    error = ErrorTypes.SHORT_BREAKOUT_ENTRY_ABOVE_CURRENT_PRICE;
  } else if (isBreakout && !isShort && entryPrice <= currentPrice) {
    error = ErrorTypes.LONG_BREAKOUT_ENTRY_BELOW_CURRENT_PRICE;
  }
  if (error) errors.push(error);
  return errors;
};

const getTakeProfitCurrentPriceValidation = (
  takeProfitPrice: number,
  isShort?: boolean,
  currentPrice?: number,
  isBreakout?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  if (!isBreakout && cancelOnFirstTargetReached && !!currentPrice) {
    if (isShort && takeProfitPrice > currentPrice)
      return ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_CURRENT_PRICE;
    else if (!isShort && takeProfitPrice < currentPrice)
      return ErrorTypes.LONG_TAKE_PROFIT_BELOW_CURRENT_PRICE;
  }
};

const validateTakeProfitOrderRelativePrice = (
  takeProfitPrice: number,
  isShort: boolean,
  entriesMinMaxPrices: { min: number; max: number },
  currentPrice?: number,
  isBreakout?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  const errors: ErrorTypes[] = [];
  let error: ErrorTypes | undefined;
  if (isShort && takeProfitPrice >= entriesMinMaxPrices.min) {
    error = ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_ENTRIES;
  } else if (!isShort && takeProfitPrice <= entriesMinMaxPrices.max) {
    error = ErrorTypes.LONG_TAKE_PROFIT_BELOW_ENTRIES;
  }
  if (error) errors.push(error);
  error = getTakeProfitCurrentPriceValidation(
    takeProfitPrice,
    isShort,
    currentPrice,
    isBreakout,
    cancelOnFirstTargetReached
  );
  if (error) errors.push(error);
  return errors;
};

const validateStopOrderRelativePrice = (
  stopPrice: number,
  isShort: boolean,
  currentPrice: number,
  entriesMinMaxPrices: { min: number; max: number },
  takeProfitMinMaxPrices: { min: number; max: number },
  validateStopRelativeToEntries: boolean = true,
  validateStopRelativeToTakeProfit: boolean = true,
  isBreakout?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  const errors: ErrorTypes[] = [];
  let error: ErrorTypes | undefined;
  if (validateStopRelativeToEntries) {
    if (isShort && stopPrice <= entriesMinMaxPrices.max) {
      error = ErrorTypes.SHORT_STOP_BELOW_ENTRIES;
    } else if (!isShort && stopPrice >= entriesMinMaxPrices.min) {
      error = ErrorTypes.LONG_STOP_ABOVE_ENTRIES;
    }
    if (error) errors.push(error);
  }

  if (validateStopRelativeToTakeProfit) {
    if (isShort && stopPrice <= takeProfitMinMaxPrices.max) {
      error = ErrorTypes.SHORT_TAKE_PROFIT_ABOVE_STOP;
    } else if (!isShort && stopPrice >= takeProfitMinMaxPrices.min) {
      error = ErrorTypes.LONG_TAKE_PROFIT_BELOW_STOP;
    }
    if (error) errors.push(error);
  }
  error = getStopCurrentPriceValidation(
    stopPrice,
    currentPrice,
    isShort,
    isBreakout,
    cancelOnFirstTargetReached
  ) as ErrorTypes;
  if (error) errors.push(error);
  return errors;
};

const getMinMax = (prices: number[]) => {
  return {
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
};

export const validateOrderRelativePrice = (
  orderPrice: number,
  orderType: OrderTypes,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  entryOrders: (EditOrderData | EditTargetOrZone)[],
  takeProfitOrders: (EditOrderData | EditTargetOrZone)[],
  validateStopRelativeToEntries?: boolean
) => {
  const getPricesFromOrders = (
    orders: (EditOrderData | EditTargetOrZone)[]
  ) => {
    return orders
      .flatMap((entry) => entry.edit_target_price)
      .map((num) => (typeof num === "string" ? parseFloat(num) : num));
  };
  const takeProfitsPrices = getPricesFromOrders(takeProfitOrders);
  const takeProfitsMinMaxPrices = getMinMax(takeProfitsPrices);
  if (orderType === OrderTypes.ENTRY) {
    return validateEntryOrderRelativePrice(
      orderPrice,
      isShort,
      isBreakout,
      currentPrice,
      takeProfitsMinMaxPrices
    );
  }

  const entriesPrices = getPricesFromOrders(entryOrders);
  const entriesMinMaxPrices = getMinMax(entriesPrices);
  if (orderType === OrderTypes.TAKE_PROFIT) {
    return validateTakeProfitOrderRelativePrice(
      orderPrice,
      isShort,
      entriesMinMaxPrices
    );
  }

  return validateStopOrderRelativePrice(
    orderPrice,
    isShort,
    currentPrice,
    entriesMinMaxPrices,
    takeProfitsMinMaxPrices,
    validateStopRelativeToEntries
  );
};

export const validateRelativeOrdersPrices = (
  entries: OrderData[] | ValidationTargetData[],
  takeProfits: OrderData[] | ValidationTargetData[],
  stops: OrderData[] | ValidationTargetData[],
  ordersErrors: OrdersErrors,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  validateStopRelativeToEntries?: boolean,
  validateStopRelativeToTakeProfit?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  const entriesPrices = entries.flatMap((entry) => entry.price);
  const takeProfitsPrices = takeProfits.flatMap(
    (takeProfit) => takeProfit.price
  );

  const takeProfitsMinMaxPrices = getMinMax(takeProfitsPrices);
  const entriesMinMaxPrices = getMinMax(entriesPrices);
  const entriesErrors = ordersErrors[OrderTypes.ENTRY];
  const takeProfitsErrors = ordersErrors[OrderTypes.TAKE_PROFIT];
  const stopsErrors = ordersErrors[OrderTypes.STOP];

  entriesPrices.forEach((entryPrice, index) => {
    validateEntryOrderRelativePrice(
      entryPrice,
      isShort,
      isBreakout,
      currentPrice,
      takeProfitsMinMaxPrices
    ).forEach((error) => addError(entriesErrors, error, index));
  });
  takeProfitsPrices.forEach((takeProfitPrice, index) => {
    validateTakeProfitOrderRelativePrice(
      takeProfitPrice,
      isShort,
      entriesMinMaxPrices,
      currentPrice,
      isBreakout,
      cancelOnFirstTargetReached
    ).forEach((error) => addError(takeProfitsErrors, error, index));
  });
  if (stops.length) {
    const stopPrice = stops[0].price as number;
    validateStopOrderRelativePrice(
      stopPrice,
      isShort,
      currentPrice,
      entriesMinMaxPrices,
      takeProfitsMinMaxPrices,
      validateStopRelativeToEntries,
      validateStopRelativeToTakeProfit,
      isBreakout,
      cancelOnFirstTargetReached
    ).forEach((error) => addError(stopsErrors, error, 0));
  }
};

export const validateNumberOfBreakoutOrders = (
  entries: (ValidationOrderData | ValidationTargetData)[],
  ordersErrors: OrdersErrors
) => {
  entries
    .slice(2)
    .map((_, index) =>
      addError(
        ordersErrors[OrderTypes.ENTRY],
        ErrorTypes.BREAKOUT_TOO_MANY_ENTRIES,
        index
      )
    );
};

export const validateOrdersPrices = (
  ordersData: ValidationOrdersData | ValidationTargetsData,
  errors: OrdersErrors,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  minPrice: number,
  maxPrice: number,
  validateStopRelativeToEntries?: boolean,
  validateStopRelativeToTakeProfit?: boolean,
  cancelOnFirstTargetReached?: boolean
) => {
  let orderType: keyof InputOrdersData;
  const filterPendingOrders = (
    orders: (ValidationOrderData | ValidationTargetData)[]
  ) =>
    orders.filter((order) => {
      const isTargetZone = Array.isArray(order.price);

      return (
        !("status" in order) ||
        order.status === OrderStatus.PENDING ||
        (orderHasStatus(
          order as { status: OrderStatus },
          false,
          OrderStatus.PENDING,
          OrderStatus.PARTIALLY_FULFILLED
        ) &&
          isTargetZone)
      );
    });
  for (orderType in ordersData) {
    const ordersForValidation = filterPendingOrders(ordersData[orderType]);
    validateOrdersDuplicatePrices(ordersForValidation, errors[orderType]);
    validateOrdersPricesRange(
      ordersForValidation,
      errors[orderType],
      minPrice,
      maxPrice
    );
  }

  validateRelativeOrdersPrices(
    filterPendingOrders(ordersData[OrderTypes.ENTRY]),
    filterPendingOrders(ordersData[OrderTypes.TAKE_PROFIT]),
    filterPendingOrders(ordersData[OrderTypes.STOP]),
    errors,
    isShort,
    isBreakout,
    currentPrice,
    validateStopRelativeToEntries,
    validateStopRelativeToTakeProfit,
    cancelOnFirstTargetReached
  );
};

export const validateOrdersPercentages = (
  ordersData: ValidationOrdersData | ValidationTargetsData,
  ordersErrors: OrdersErrors,
  isTargetValidation?: boolean
) => {
  const roundingErrorTolerance = 0.01;
  const sumOrdersPercentage = (
    unFulfilledOrders: (ValidationOrderData | ValidationTargetData)[]
  ) => sumBy(unFulfilledOrders, (orderData) => orderData.percent || 0);

  const handleTargetInvalidRatio = (
    unFulfilledOrders: (ValidationOrderData | ValidationTargetData)[],
    totalFulfilledPercentage: number
  ) => {
    unFulfilledOrders.forEach((order, index) => {
      if (
        order.percent !== undefined &&
        (order.percent < 0 ||
          order.percent >
            100 - totalFulfilledPercentage + roundingErrorTolerance)
      ) {
        addError(
          ordersErrors[orderType],
          ErrorTypes.INVALID_ORDER_RATIO,
          index
        );
      }
    });
  };
  const pendingStatuses = [OrderStatus.PENDING];
  const fulfilledStatuses = [OrderStatus.FULFILLED];
  if (isTargetValidation) pendingStatuses.push(OrderStatus.PARTIALLY_FULFILLED);
  else fulfilledStatuses.push(OrderStatus.PARTIALLY_FULFILLED);

  let orderType: keyof InputOrdersData;
  for (orderType in ordersData) {
    const orders: (ValidationOrderData | ValidationTargetData)[] =
      ordersData[orderType];

    const unFulfilledOrders = orders.filter((order) =>
      orderHasStatus(order, false, ...pendingStatuses)
    );
    const hasOrdersWithoutRatio = orders.some(
      (order) => order.percent === undefined
    );
    if (!hasOrdersWithoutRatio) {
      const fulfilledOrders = orders.filter((order) =>
        orderHasStatus(order, false, ...fulfilledStatuses)
      );
      const totalPendingPercentage = sumOrdersPercentage(unFulfilledOrders);
      const totalFulfilledPercentage = sumOrdersPercentage(fulfilledOrders);
      const gapFromFullRatio = parseFloat(
        Math.abs(
          100 - (totalFulfilledPercentage + totalPendingPercentage)
        ).toFixed(2)
      );
      if (
        unFulfilledOrders.length &&
        gapFromFullRatio > roundingErrorTolerance
      ) {
        addError(ordersErrors[orderType], ErrorTypes.SUM_OF_RATIOS);
      }
      handleTargetInvalidRatio(unFulfilledOrders, totalFulfilledPercentage);
    }
  }
};

export const validateNumberOfOrders: {
  (
    entries: ValidationOrderData[],
    ordersErrors: OrdersErrors,
    isBreakout: boolean,
    minAmount: number,
    quantity: number,
    minQuantity: number
  ): void;
  (
    entries: ValidationTargetData[],
    ordersErrors: OrdersErrors,
    isBreakout: boolean
  ): void;
} = (
  entries: (ValidationOrderData | ValidationTargetData)[],
  ordersErrors: OrdersErrors,
  isBreakout: boolean,
  minAmount?: number,
  quantity?: number,
  minQuantity?: number
) => {
  const checkAtLeastOneEntryError = () => {
    if (validEntries.length < 1) {
      addError(ordersErrors[OrderTypes.ENTRY], ErrorTypes.AT_LEAST_ONE_ENTRY);
    }
  };

  const validEntries = entries.filter(
    (order) => order.status !== OrderStatus.CANCELLED
  );
  const isTradeOrderValidation = [minAmount, quantity, minQuantity].every(
    (param) => param !== undefined
  );
  if (isBreakout) {
    validateNumberOfBreakoutOrders(entries, ordersErrors);
  }
  if (!isTradeOrderValidation) {
    checkAtLeastOneEntryError();
    return;
  }

  const tradeAmount = sumBy(
    validEntries as ValidationOrderData[],
    (target) => target.amount || 0
  );
  const isInvalidTrade =
    (tradeAmount < minAmount! && quantity! < minQuantity!) ||
    (!tradeAmount && !quantity);
  if (isInvalidTrade) {
    checkAtLeastOneEntryError();
  }
};

export const validateOrders = (
  ordersData: InputOrdersData,
  isShort: boolean,
  isBreakout: boolean,
  currentPrice: number,
  availableAmount: number,
  minAmount: number,
  minPrice: number,
  maxPrice: number,
  getMinAmountByPrice: (price: number) => number,
  minQuantity: number,
  selectedQuantity: number,
  cancelOnFirstTargetReached?: boolean
): OrdersErrors => {
  const numberOrdersData = getNumberOrdersData(ordersData);
  const errors = Object.entries(ordersData).reduce(
    (acc, [orderType, ordersData]) => {
      acc[orderType as OrderTypes] = getDefaultOrdersSectionErrors(ordersData);
      return acc;
    },
    {} as OrdersErrors
  );

  validateOrdersPrices(
    numberOrdersData,
    errors,
    isShort,
    isBreakout,
    currentPrice,
    minPrice,
    maxPrice,
    true,
    true,
    cancelOnFirstTargetReached
  );
  validateNumberOfOrders(
    numberOrdersData[OrderTypes.ENTRY],
    errors,
    isBreakout,
    minAmount,
    selectedQuantity,
    minQuantity
  );
  validateOrdersPercentages(numberOrdersData, errors);
  validateEntryOrdersAmounts(
    numberOrdersData[OrderTypes.ENTRY],
    errors[OrderTypes.ENTRY],
    availableAmount,
    getMinAmountByPrice
  );

  return errors;
};

export const findDuplicates = (array: (number | [number, number])[]) =>
  array.filter(
    (item, index) =>
      findIndex(array, (current) => isEqual(current, item)) !== index
  );

export const getValidationMessage = (min?: number, max?: number) =>
  `${min ? "Min: " + min.toString() + " " : ""}${
    max ? "Max: " + max.toString() : ""
  }`;

export const getAmountValidationMessage = (
  min?: number,
  max?: number,
  coin?: string,
  isMargin?: boolean,
  useOldVersion?: boolean
) => {
  if (!useOldVersion && min !== undefined && max !== undefined && min > max) {
    return `Based on the selected entries strategy the required amount should be ${min}${
      coin ? " " + coin : ""
    }. Please ${
      isMargin ? "modify the leverage or" : ""
    } change the entries strategy.`;
  } else {
    return getValidationMessage(min, max);
  }
};

export const validateGeneralTradeData = (
  amount: number | string,
  symbol: string | undefined,
  availableAmount: number,
  minAmount: number,
  quantity: number | string | undefined,
  availableQuantity: number,
  minQuantity: number,
  orders: InputOrdersData,
  accountName?: string,
  extraInfo?: ExtraInfo,
  generalData?: GetCreateTradeGeneralDataResponse,
  currentPrice?: number
): ErrorTypeToDisplay => {
  const errors: ErrorTypeToDisplay = {};

  const generalInfo = generalData?.data?.general?.info;
  const limits = generalData?.limits;
  const maxTrades = limits?.max_trades;
  const leverageLimits = limits?.leverage;
  const generalOptions = generalData?.data?.general?.options || [];
  const leverageOptions = getOptionsByCreateTradeKey(
    generalOptions,
    "leverage"
  );
  const maxLeverage = leverageOptions[leverageOptions.length - 1]?.value as
    | number
    | undefined;
  const numOpenLongTrades = generalInfo?.long_trades || 0;
  const numOpenShortTrades = generalInfo?.short_trades || 0;

  if (
    maxTrades !== undefined &&
    numOpenLongTrades + numOpenShortTrades >= maxTrades
  ) {
    errors[
      ErrorTypes.MAX_NUM_TRADES_EXCEEDED
    ] = `You can’t have more than ${maxTrades} open ${symbol} trades in ${accountName}`;
  }

  let numberAmount = amount;
  if (typeof numberAmount === "string") {
    numberAmount = parseFloat(numberAmount);
  }
  let numberQuantity = quantity;
  if (typeof numberQuantity === "string") {
    numberQuantity = parseFloat(numberQuantity);
  }

  if (
    numberAmount &&
    (numberAmount > availableAmount || numberAmount < minAmount)
  ) {
    errors[ErrorTypes.INVALID_TRADE_AMOUNT] = undefined;
  }

  if (
    numberQuantity &&
    (numberQuantity > availableQuantity || numberQuantity < minQuantity)
  ) {
    errors[ErrorTypes.INVALID_TRADE_QUANTITY] = undefined;
  }

  if (leverageLimits) {
    if (extraInfo?.margin_type !== leverageLimits.margin_type) {
      errors[ErrorTypes.INVALID_LEVERAGE_MARGIN_TYPE] = undefined;
    }
    if (extraInfo?.leverage! < leverageLimits.min_multiplier) {
      errors[ErrorTypes.INVALID_LEVERAGE_MULTIPLIER] = undefined;
    }
  }
  if (
    !!extraInfo?.leverage &&
    maxLeverage &&
    extraInfo?.leverage > maxLeverage
  ) {
    errors[ErrorTypes.INVALID_LEVERAGE_MULTIPLIER] = undefined;
  }

  if (!numberAmount && !numberQuantity) {
    errors[ErrorTypes.INVALID_TRADE_AMOUNT] = undefined;
    errors[ErrorTypes.INVALID_TRADE_QUANTITY] = undefined;
  }

  if (
    quantity &&
    orders[OrderTypes.ENTRY].length === 0 &&
    orders[OrderTypes.TAKE_PROFIT].length === 0 &&
    orders[OrderTypes.STOP].length === 0
  ) {
    errors[ErrorTypes.INVALID_NUMBER_OF_TAKE_PROFIT_AND_STOP] = undefined;
  }

  orders[OrderTypes.TAKE_PROFIT].forEach((takeProfit) => {
    const error = getTakeProfitCurrentPriceValidation(
      parseFloat(takeProfit.price),
      extraInfo?.reverse,
      currentPrice,
      extraInfo?.trade_type === TradeTypes.BREAKOUT,
      extraInfo?.cancel_on_first_target_reached
    );
    if (error) errors[error] = undefined;
  });

  if (orders[OrderTypes.STOP].length && currentPrice && extraInfo) {
    const stopPrice = parseFloat(orders[OrderTypes.STOP][0].price);
    const error = getStopCurrentPriceValidation(
      stopPrice,
      currentPrice,
      extraInfo.reverse,
      extraInfo?.trade_type === TradeTypes.BREAKOUT,
      extraInfo?.cancel_on_first_target_reached
    );
    if (error) errors[error] = undefined;
  }

  return errors;
};

export const validateNumLongShortTrades = (
  canOpenBothDirections: boolean,
  isShort: boolean,
  rawAvailableQuantity: number,
  generalInfo?: CreateTradeGeneralInfo
): string | undefined => {
  enum ErrorObjectTypes {
    POSITION = "position",
    TRADE = "trade",
  }

  if (canOpenBothDirections) return;
  if (isShort) {
    if (rawAvailableQuantity > 0) return ErrorObjectTypes.POSITION;
    else if ((generalInfo?.long_trades || 0) > 0) return ErrorObjectTypes.TRADE;
  } else if (!isShort) {
    if (rawAvailableQuantity < 0) return ErrorObjectTypes.POSITION;
    else if ((generalInfo?.short_trades || 0) > 0)
      return ErrorObjectTypes.TRADE;
  }
};

export const getOrdersWithPrecision = (
  orders: InputOrderData[],
  percentPrecision?: number
) => {
  return orders.map((order) => ({
    ...order,
    percent: toFixed(order.percent, percentPrecision).toString(),
  }));
};

export const getNumOrdersValidationMessage = (
  numOrders: number,
  orderType: OrderTypes,
  isBreakout?: boolean,
  limits?: CreateTradeLimits | EditSignalLimits,
  isSignal?: boolean
): string | undefined => {
  const numberOfTargets = limits?.number_of_targets;
  let limit: number | undefined;
  if (orderType === OrderTypes.ENTRY) {
    limit = numberOfTargets?.regular_entries;
  } else if (orderType === OrderTypes.TAKE_PROFIT) {
    limit = numberOfTargets?.regular;
  } else {
    limit = numberOfTargets?.stop;
  }
  let messageSuffix = "";
  if (isBreakout && orderType === OrderTypes.ENTRY) {
    limit = numberOfTargets?.breakout_entries || limit;
    const signalOrTradeType = isSignal ? "signal" : "trade";
    messageSuffix = ` in a breakout ${signalOrTradeType}`;
  }

  let message = "";
  if (limit && numOrders >= limit) {
    const targetOrOrderType = isSignal ? "target" : "order";
    message = `You can't add more than ${limit} ${ORDER_TYPE_TO_TITLE_DISPLAY[
      orderType
    ].toLowerCase()} ${targetOrOrderType}${
      limit > 1 ? "s" : ""
    }${messageSuffix}`;
  }

  return message;
};

export const getOptionsByCreateTradeKey = (
  generalOptions: NameAndOptions[],
  createTradeKey: string
) => {
  const generalOptionName =
    CREATE_TRADE_KEY_TO_GENERAL_OPTIONS_NAME[createTradeKey];
  return (
    generalOptions.find(
      (generalOption) => generalOption[0].toLowerCase() === generalOptionName
    )?.[1] || []
  );
};

export const getPriceValidationMessage = (
  currentPrice?: number,
  precision?: number,
  priceFactorLimits?: { min: number; max: number }
) => {
  if (currentPrice) {
    const { min, max } = getMinAndMaxPrices(
      currentPrice,
      precision || 0,
      priceFactorLimits
    );
    return `Price should be between ${min} and ${max}`;
  }
};

export const getOrdersWithUpdatedDistance = (
  orders: InputOrderData[],
  weightedAveragePrice: number,
  percentPrecision?: number
) =>
  orders.map((order) => {
    return {
      ...order,
      distance:
        weightedAveragePrice > 0
          ? calculateDistanceFromPrice(
              parseFloat(order.price),
              weightedAveragePrice,
              percentPrecision
            ).toString()
          : "",
    };
  });

export const updateOrdersWithAmount = (
  orders: InputOrderData[],
  amount: number,
  precisionConfig?: PrecisionConfig
): InputOrderData[] => {
  return orders.map((order) => ({
    ...order,
    amount: toFixed(
      (parseFloat(order.percent) / 100) * (amount || 0),
      precisionConfig?.amount
    ).toString(),
  }));
};

export const buildOrdersFromFreeTextData = (
  freeTextTradeData: FreeTextTradeData,
  tradeAmount: number,
  entriesData: StrategyInfo,
  takeProfitsData: StrategyInfo,
  getBaselineEntryPrice: (orderType: OrderTypes) => number,
  isShort: boolean,
  isBreakout: boolean,
  precisionConfig?: PrecisionConfig
) => {
  const orders: InputOrdersData = {
    [OrderTypes.ENTRY]: [],
    [OrderTypes.TAKE_PROFIT]: [],
    [OrderTypes.STOP]: [],
  };
  const orderTypeToStrategy = {
    [OrderTypes.ENTRY]: {
      numOrders: entriesData.numOrders,
      strategy: entriesData.strategy,
    },
    [OrderTypes.TAKE_PROFIT]: {
      numOrders: takeProfitsData.numOrders,
      strategy: takeProfitsData.strategy,
    },
    [OrderTypes.STOP]: {
      numOrders: 1,
      strategy: [100],
    },
  };

  const freeTextNameToOrderType = {
    buy: OrderTypes.ENTRY,
    sell: OrderTypes.TAKE_PROFIT,
    stop: OrderTypes.STOP,
  };
  type FreeTextOrderTypes = keyof typeof freeTextNameToOrderType;

  const getSectionKey = (freeTextOrderTypeName: string) =>
    (freeTextOrderTypeName + "_section") as keyof Pick<
      FreeTextTradeData,
      "buy_section" | "sell_section" | "stop_section"
    >;
  const getZoneKey = (freeTextOrderTypeName: string) =>
    (freeTextOrderTypeName + "_zone") as keyof Pick<
      FreeTextTradeData,
      "buy_zone" | "sell_zone"
    >;

  Object.keys(freeTextNameToOrderType).forEach(
    (freeTextOrderTypeName: string) => {
      const section = freeTextTradeData[getSectionKey(freeTextOrderTypeName)];
      let zone: number[] = [];
      if (freeTextOrderTypeName !== "stop") {
        zone = freeTextTradeData[getZoneKey(freeTextOrderTypeName)];
      }
      const orderType =
        freeTextNameToOrderType[freeTextOrderTypeName as FreeTextOrderTypes];
      const strategyInfo = orderTypeToStrategy[orderType];
      const priceTargets = typeof section === "number" ? [section] : section;
      let numOrders = strategyInfo.numOrders;
      if (section) {
        numOrders = priceTargets?.length;
      } else if (zone) {
        if (zone[0] === zone[1]) {
          numOrders = EQUAL_ZONE_PRICES_NUM_ORDERS;
        } else if (isBreakout && orderType === OrderTypes.ENTRY) {
          numOrders = DEFAULT_BREAKOUT_ENTRY_NUM_ORDERS;
        } else {
          numOrders = DEFAULT_NUM_ORDERS;
        }
      }

      if (section || zone?.length) {
        orders[orderType] = buildOrders(
          numOrders,
          strategyInfo.strategy || [],
          tradeAmount,
          precisionConfig?.percent || 0,
          precisionConfig?.price || 0,
          precisionConfig?.amount || 0,
          orderType,
          getBaselineEntryPrice(orderType),
          isShort,
          isBreakout,
          !section && zone?.length
            ? {
                min: zone[0],
                max: zone?.[1] || zone[0],
              }
            : undefined,
          priceTargets
        );
      } else {
        orders[orderType] = [];
      }
    }
  );
  return orders;
};

export const getOptionText = (
  value: string,
  prefix: string,
  itemOption: Option,
  suffix: string,
  delimiter = ""
) =>
  (value && prefix ? prefix + delimiter : "") +
  (itemOption.name || value) +
  (value && suffix ? delimiter + suffix : "");

export const getOrdersWithExisting = (
  entryOrders: InputOrderData[],
  existingQuantityOrder: InputOrderData | undefined
) => {
  let ordersForWeightedAverage = entryOrders;
  if (existingQuantityOrder) {
    ordersForWeightedAverage = [existingQuantityOrder, ...entryOrders];
    const tradeAmountWithExisting = ordersForWeightedAverage.reduce(
      (sum, order) => sum + parseFloat(order.amount || "0"),
      0
    );
    ordersForWeightedAverage = ordersForWeightedAverage.map((order) => ({
      ...order,
      percent: (
        (parseFloat(order.amount || "0") / tradeAmountWithExisting) *
        100
      ).toString(),
    }));
  }
  return ordersForWeightedAverage;
};

export const getResolveWarningMessage = (
  orderType: OrderTypes,
  isSignal?: boolean
) => {
  return `This action will modify your ${ORDER_TYPE_TO_TITLE_DISPLAY[
    orderType
  ].toLowerCase()} ${
    isSignal ? "target" : "order"
  } ratios such that their sum will be 100% and valid. Are you sure you wish to continue?`;
};
